import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { usePageTitle } from "~/hooks/usePageTitle";

import { apiClient } from "../../api/ApiClient";
import ApiKeysTable from "../../components/apiKeys/ApiKeysTable";
import { PaginationConfig } from "../../components/ui/pagination/Pagination";
import styles from "./ApiKeysPage.module.scss";

const ApiKeysPage = () => {
  usePageTitle(PAGE_TITLES.apiKeys);
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  const { mutate: deleteApiKey, isPending: isPendingApiKeyDelete } =
    useMutation({
      mutationFn: async (id: number) => {
        await apiClient.accessTokens.accessTokensDelete(id);
      },
      onSettled: () => {
        enqueueSnackbar("API Key successfully deleted", {
          variant: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
      },
      onError: () => {
        enqueueSnackbar("Error happened", {});
      },
    });
  const { mutate: createApiKey, isPending: isPendingApiKeyCreate } =
    useMutation({
      mutationFn: async () => {
        await apiClient.accessTokens.accessTokensCreate();
      },
      onSettled: () => {
        enqueueSnackbar("API Key successfully created", {
          variant: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["apiKeys"] });
      },
      onError: () => {
        enqueueSnackbar("Error happened", {
          variant: "error",
          persist: true,
        });
      },
    });
  const { data, isLoading, isFetched } = useQuery({
    queryKey: [
      "apiKeys",
      pagination.itemsPerPage,
      pagination.currentPage,
      team?.id,
    ],
    enabled: Boolean(team?.id),
    queryFn: async () => {
      const response = await apiClient.accessTokens.accessTokensList({
        "page[size]": pagination.itemsPerPage,
        "page[number]": pagination.currentPage,
        s: "created_at DESC",
      });

      const { headers } = response;

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));

      return response.data.access_tokens;
    },
  });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.apiKeys}</h1>
      <ApiKeysTable
        isFetched={isFetched}
        data={data || []}
        isLoading={isLoading}
        pagination={pagination}
        setPagination={setPagination}
        onDelete={deleteApiKey}
        onCreate={createApiKey}
        isPendingApiKeyCreate={isPendingApiKeyCreate}
        isPendingApiKeyDelete={isPendingApiKeyDelete}
      />
    </div>
  );
};
export default ApiKeysPage;
